import type { AlertTemplate, AlertCategoryInfo } from '../types'

/** 8 predefined alert templates with business intelligence */
export const ALERT_TEMPLATES: AlertTemplate[] = [
  {
    id: 'tpl-001',
    name: 'Discount Crescendo Inconsistency',
    severity: 'high',
    frequency: 'occasional',
    category: 'pricing_strategy',
    triggerDescription:
      'Detects when upsell/downsell discount progression is not following a crescendo pattern (each subsequent offer should have a higher discount)',
    businessImpact:
      'Non-crescendo discounts reduce conversion rates by 15-25% as customers see decreasing value in subsequent offers',
    recommendedAction:
      'Review funnel step order and ensure discounts increase progressively: Upsell 1 < Upsell 2 < Downsell 1',
    conditionsCount: 3,
    isActive: true,
  },
  {
    id: 'tpl-002',
    name: 'Sub-$12 Pricing Alert',
    severity: 'critical',
    frequency: 'rare',
    category: 'pricing_strategy',
    triggerDescription:
      'Triggers when any offer final price falls below $12.00, which may indicate a misconfigured discount or pricing error',
    businessImpact:
      'Sub-$12 pricing can result in negative margin after payment processing fees, shipping costs, and COGS',
    recommendedAction:
      'Verify the discount configuration and ensure final price covers minimum margin requirements ($12+ for US market)',
    conditionsCount: 2,
    isActive: true,
  },
  {
    id: 'tpl-003',
    name: 'Low AOV Transaction Alert',
    severity: 'medium',
    frequency: 'occasional',
    category: 'pricing_strategy',
    triggerDescription:
      'Fires when the average order value for a funnel drops below the target AOV threshold ($45 for Shapermint)',
    businessImpact:
      'Low AOV indicates the funnel is not effectively upselling, reducing profitability per customer acquisition',
    recommendedAction:
      'Add higher-value upsell offers, bundle products, or adjust free shipping thresholds to incentivize larger orders',
    conditionsCount: 2,
    isActive: false,
  },
  {
    id: 'tpl-004',
    name: 'Mismatched Booster Naming',
    severity: 'high',
    frequency: 'rare',
    category: 'free_gifts_boosters',
    triggerDescription:
      'Detects when booster/free gift offer names don\'t match the associated product names in the catalog',
    businessImpact:
      'Mismatched names create customer confusion and increase support tickets by ~20% for affected funnels',
    recommendedAction:
      'Sync offer names with product catalog names. Use the naming convention: "[Product Name] - Free Gift"',
    conditionsCount: 4,
    isActive: true,
  },
  {
    id: 'tpl-005',
    name: 'Incomplete Booster Strategy',
    severity: 'medium',
    frequency: 'occasional',
    category: 'free_gifts_boosters',
    triggerDescription:
      'Alerts when a funnel with >$50 AOV target lacks a free gift/booster step to incentivize checkout completion',
    businessImpact:
      'Funnels without boosters see 10-18% lower checkout completion rates compared to funnels with strategic free gifts',
    recommendedAction:
      'Add a free gift step positioned after the first upsell. Recommended: low-cost, high-perceived-value items',
    conditionsCount: 3,
    isActive: false,
  },
  {
    id: 'tpl-006',
    name: 'Missing URL Rule in Free Gift Config',
    severity: 'medium',
    frequency: 'frequent',
    category: 'free_gifts_boosters',
    triggerDescription:
      'Detects free gift configurations that lack URL-based targeting rules, potentially showing gifts on wrong pages',
    businessImpact:
      'Untargeted free gifts appear on irrelevant pages, diluting offer effectiveness and confusing customers',
    recommendedAction:
      'Add page_url trigger rules to restrict free gift visibility to relevant product/landing pages only',
    conditionsCount: 2,
    isActive: true,
  },
  {
    id: 'tpl-007',
    name: 'Leads Pop-Up Timing Error',
    severity: 'high',
    frequency: 'occasional',
    category: 'lead_generation',
    triggerDescription:
      'Fires when lead capture pop-up timing is set to <3 seconds or >60 seconds, both being suboptimal ranges',
    businessImpact:
      'Pop-ups under 3s annoy visitors (80% bounce increase). Over 60s misses 70% of visitors who leave earlier',
    recommendedAction:
      'Set pop-up timing between 5-15 seconds for optimal lead capture. Use exit-intent as secondary trigger',
    conditionsCount: 3,
    isActive: true,
  },
  {
    id: 'tpl-008',
    name: 'Missing Post-Checkout Offer Steps',
    severity: 'medium',
    frequency: 'frequent',
    category: 'funnel_structure',
    triggerDescription:
      'Detects funnels that complete after checkout without any post-purchase upsell or cross-sell steps',
    businessImpact:
      'Post-checkout offers generate 10-30% additional revenue with minimal acquisition cost. Missing them leaves money on the table',
    recommendedAction:
      'Add 1-2 post-checkout upsell steps with complementary products. Recommended: "Add to your order" with one-click purchase',
    conditionsCount: 2,
    isActive: true,
  },
]

/** Category metadata with descriptions */
export const ALERT_CATEGORIES: AlertCategoryInfo[] = [
  {
    id: 'pricing_strategy',
    name: 'Pricing Strategy',
    description: 'Monitor discount structures, AOV targets, and margin protection',
    templateCount: 3,
    activeCount: 2,
  },
  {
    id: 'free_gifts_boosters',
    name: 'Free Gifts & Boosters',
    description: 'Track booster configurations, naming consistency, and targeting rules',
    templateCount: 3,
    activeCount: 2,
  },
  {
    id: 'lead_generation',
    name: 'Lead Generation',
    description: 'Optimize lead capture timing, pop-up rules, and conversion triggers',
    templateCount: 1,
    activeCount: 1,
  },
  {
    id: 'funnel_structure',
    name: 'Funnel Structure',
    description: 'Validate funnel completeness, step ordering, and post-checkout flows',
    templateCount: 1,
    activeCount: 1,
  },
  {
    id: 'inventory_stock',
    name: 'Inventory & Stock',
    description: 'Monitor product availability and stock-based offer adjustments',
    templateCount: 0,
    activeCount: 0,
  },
]

/** Get templates filtered by category */
export function getTemplatesByCategory(category: string): AlertTemplate[] {
  return ALERT_TEMPLATES.filter((t) => t.category === category)
}

/** Get active template count */
export function getActiveTemplateCount(): number {
  return ALERT_TEMPLATES.filter((t) => t.isActive).length
}

/** Get critical active count */
export function getCriticalActiveCount(): number {
  return ALERT_TEMPLATES.filter((t) => t.isActive && (t.severity === 'critical' || t.severity === 'high')).length
}

/** Calculate coverage percentage (active / total * 100) */
export function getCoveragePercent(): number {
  if (ALERT_TEMPLATES.length === 0) return 0
  return Math.round((getActiveTemplateCount() / ALERT_TEMPLATES.length) * 100)
}
